'use client';
import { useState } from 'react';
import { newsletters } from '@/lib/data';
import Link from 'next/link';

export default function NewslettersPage() {
  const [emails, setEmails] = useState<Record<string, string>>({});
  const [subscribed, setSubscribed] = useState<Record<string, boolean>>({});

  function handleSubscribe(id: string) {
    if (emails[id]?.trim()) {
      setSubscribed(prev => ({ ...prev, [id]: true }));
    }
  }

  const benefits = [
    { title: 'Expert curation', desc: 'Every newsletter is selected by section editors, not algorithms.' },
    { title: 'Zero spam', desc: 'We never share your data. Unsubscribe instantly, any time.' },
    { title: 'Works everywhere', desc: 'Read in your inbox, on the app, or online at veritas.news.' },
  ];

  return (
    <div>
      {/* HERO */}
      <div className="bg-[#1A1A1A] py-16 px-5 text-center">
        <h1 className="font-['Playfair_Display',Georgia,serif] font-bold text-[36px] lg:text-[44px] text-white mb-4">
          Stay informed. Subscribe free.
        </h1>
        <p className="font-['Source_Serif_4',Georgia,serif] text-[18px] text-white/80 max-w-[560px] mx-auto leading-[1.6]">
          From our award-winning correspondents to our expert analysts — the news and analysis that matters, delivered when you want it.
        </p>
      </div>

      {/* NEWSLETTER GRID */}
      <div className="max-w-[1400px] mx-auto px-5 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {newsletters.map(nl => (
            <div key={nl.id} className="border border-[#D4D4D4] p-6 bg-white hover:border-[#0D0D0D] transition-colors">
              {/* Icon */}
              <div className="w-11 h-11 bg-[#F5F4F0] flex items-center justify-center mb-4">
                <span className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[18px] text-[#6B6B6B]">
                  {nl.name[0]}
                </span>
              </div>
              {/* Name + frequency */}
              <div className="flex items-center gap-3 mb-2">
                <span className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[16px] text-[#0D0D0D]">{nl.name}</span>
                <span className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[10px] uppercase tracking-[0.08em] bg-[#F5F4F0] px-2 py-0.5 text-[#6B6B6B]">
                  {nl.frequency}
                </span>
              </div>
              <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[13px] text-[#6B6B6B] leading-[1.5] mb-2">
                {nl.description}
              </p>
              <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[12px] text-[#9A9A9A] mb-4">
                {nl.subscriberCount}
              </p>
              {subscribed[nl.id] ? (
                <div className="bg-[#F5F4F0] text-[#0C7A6E] font-['IBM_Plex_Sans',system-ui,sans-serif] font-semibold text-[13px] px-4 py-2.5 text-center">
                  ✓ Subscribed
                </div>
              ) : (
                <div className="flex border border-[1.5px] border-[#0D0D0D] overflow-hidden">
                  <input
                    type="email"
                    placeholder="Your email"
                    value={emails[nl.id] || ''}
                    onChange={e => setEmails(prev => ({ ...prev, [nl.id]: e.target.value }))}
                    onKeyDown={e => e.key === 'Enter' && handleSubscribe(nl.id)}
                    className="flex-1 px-3 py-2 font-['IBM_Plex_Sans',system-ui,sans-serif] text-[13px] outline-none bg-transparent"
                  />
                  <button
                    onClick={() => handleSubscribe(nl.id)}
                    className="bg-[#C8102E] text-white px-4 py-2 font-['IBM_Plex_Sans',system-ui,sans-serif] font-semibold text-[12px] hover:bg-[#9E0C23] transition-colors whitespace-nowrap"
                  >
                    Subscribe
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* APP SECTION */}
      <div className="bg-[#1A1A1A] grid grid-cols-1 md:grid-cols-2 gap-0">
        <div className="py-12 px-12 text-white">
          <h2 className="font-['Playfair_Display',Georgia,serif] font-bold text-[32px] mb-4">Read Veritas anywhere</h2>
          <ul className="space-y-3 mb-6">
            {['Offline reading — save stories to read without signal', 'Breaking alerts — pushed to you the moment news breaks', 'Ad-lite experience — focus on the journalism'].map(f => (
              <li key={f} className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[14px] text-white/80">• {f}</li>
            ))}
          </ul>
          <div className="flex gap-3 flex-wrap mt-6">
            {['App Store', 'Google Play'].map(store => (
              <button key={store} className="border-2 border-white/40 px-5 py-2.5 font-['IBM_Plex_Sans',system-ui,sans-serif] font-semibold text-[13px] text-white hover:bg-white hover:text-[#1A1A1A] transition-all">
                {store}
              </button>
            ))}
          </div>
        </div>
        <div className="bg-[#0D0D0D] py-12 px-12 flex items-center justify-center">
          <div className="w-40 border-2 border-[#333] aspect-[9/19] flex items-center justify-center text-[#555] font-['IBM_Plex_Sans',system-ui,sans-serif] text-[12px]">
            App Preview
          </div>
        </div>
      </div>

      {/* BENEFITS */}
      <div className="bg-[#F5F4F0] py-12 px-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-[1400px] mx-auto">
          {benefits.map(b => (
            <div key={b.title} className="border-t-[2.5px] border-[#0D0D0D] pt-5">
              <h3 className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[15px] mb-2">{b.title}</h3>
              <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[13px] text-[#6B6B6B] leading-[1.5]">{b.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* PRIVACY NOTE */}
      <div className="text-center py-6 font-['IBM_Plex_Sans',system-ui,sans-serif] text-[12px] text-[#9A9A9A]">
        We respect your privacy. Unsubscribe at any time.{' '}
        <Link href="/about" className="text-[#1A4A8A] hover:underline">Privacy policy</Link>
      </div>
    </div>
  );
}
